import { create } from "zustand";
import Auth from "src/Auth";
import { ANNOTATION_COORDINATE_SPACE, annotationHasPosition, renderedProseText, resolveSourceAnnotation } from "src/components/BenchmarkTakeover/comparisonProse";

// ---------------------------------------------------------------------------
// Benchmark takeover store. Selecting Muse 2 Alpha in Write or Draft starts
// a blinded two-sample comparison; the server announces this with a
// `benchmarkSessionId` chunk in
// the normal generation stream. This store owns the full takeover flow:
// streamed sample text, the 3-stage read/rate/compare state machine, rating
// draft, integrity telemetry, and all /api/benchmark traffic.
// See docs/benchmark-mode/SPEC.md and BenchmarkTakeover/.
// ---------------------------------------------------------------------------

export const BENCHMARK_CRITERIA = [
  {
    key: "characters_dialogue",
    title: "Characters & Dialogue",
    description:
      "Do characters behave and speak like specific people with consistent psychology, habits, purposeful dialogue, rather than generic story roles?",
  },
  {
    key: "earned_emotion",
    title: "Earned Emotion",
    description: "Do emotional moments feel grounded in the scene, relationship, and prior context?",
  },
  {
    key: "shows_not_tells",
    title: "Shows, Doesn’t Explain",
    description:
      "Is inner life conveyed through perception, body, and action with meaning left to infer rather than stated outright? Are characters living their lives or narrating them?",
  },
  {
    key: "tension_momentum",
    title: "Tension & Momentum",
    description: "Does each beat add forward motion or pressure that makes you want to keep reading?",
  },
  {
    key: "followed_direction",
    title: "Followed My Direction",
    description: "Does it follow your prompt, beats, POV, tense, and continuity?",
  },
  {
    key: "sounds_like_me",
    title: "Sounds Like My Story",
    description: "Does it match your style, voice, and tone without drifting into a generic model voice?",
  },
];

const GENERATION_FAILED_MESSAGE = "We couldn't finish this comparison.";
const SUBMIT_RETRY_MESSAGE = "Could not submit your feedback. Please try again.";
// Preserve feedback even when a session can no longer accept a rating.
const SESSION_CLOSED_MESSAGES = {
  expired: "This comparison session expired, so we couldn't save your feedback.",
  already_rated: "This comparison was already rated, so we couldn't save it again.",
};

// Write-again replayer is intentionally non-reactive module state: nothing
// renders from it and it mutates on streams.
let onWriteAgainRef = null;
// Aborts the in-flight generation for the current session (set per session).
let onAbortRef = null;
// A restored session can reuse its server ID but owns a new UI lifecycle.
let sessionRevision = 0;

const initialSessionState = () => ({
  sessionId: null,
  surface: null, // "draft" | "write"
  // Write only: the manuscript text the models continued from, shown faded
  // above each option so continuity can be judged.
  precedingText: "",
  // closed | loading | A | B | compare | thanks
  stage: "closed",
  samples: { A: { text: "" }, B: { text: "" } },
  sampleRevisions: { A: 0, B: 0 },
  previousSampleDrafts: { A: null, B: null },
  sampleDone: { A: false, B: false },
  generationDone: false,
  // When the comparison began; drives the stand-in progress steps.
  startedAt: 0,
  // { message } — the takeover component toasts this and closes.
  failure: null,
  // A comparison can fail after the selected model completed successfully.
  // In that case Draft inserts that sample normally instead of discarding it.
  fallback: null, // { sample: "A" | "B" }
  // Card model of an intercepted Write placeholder so TiptapDocumentSB can
  // remove it from the right bar.
  interceptedCard: null,
  scores: { A: {}, B: {} },
  aiIsms: { A: "", B: "" },
  annotations: [], // [{ sample, tag, text, custom? }]
  choice: null, // "A" | "B" | "neither"
  // The choice that was actually sent with the rating. Insertion and /keep use
  // this, never the live `choice`, so a flip while the request is in flight
  // cannot insert one option after recording the other.
  submittedChoice: null,
  submittedKeptOutput: null,
  decisionReason: "",
  // telemetry accumulators
  readMs: { A: 0, B: 0 },
  copyAttempts: 0,
  feedbackPasteEvents: 0,
  focusBlurCount: 0,
  revealedAt: null,
  canWriteAgain: false,
  submitting: false,
  submitError: null, // gentle inline message; keeps the takeover open
  ratingClosedMessage: null,
  ratingSubmitted: false,
  refund: null,
});

// A replacement is a different piece of writing. Keep the abandoned review
// locally for recovery, but never attach its feedback to the new candidate.
export function replacementSampleState(state, sample, revision) {
  return {
    samples: { ...state.samples, [sample]: { text: "" } },
    sampleRevisions: { ...state.sampleRevisions, [sample]: revision },
    previousSampleDrafts: { ...state.previousSampleDrafts, [sample]: {
      revision: state.sampleRevisions?.[sample] || 0,
      text: state.samples[sample].text,
      scores: state.scores[sample],
      aiIsms: state.aiIsms[sample],
      annotations: state.annotations.filter((annotation) => annotation.sample === sample),
      readMs: state.readMs[sample],
    } },
    sampleDone: { ...state.sampleDone, [sample]: false },
    scores: { ...state.scores, [sample]: {} },
    aiIsms: { ...state.aiIsms, [sample]: "" },
    annotations: state.annotations.filter((annotation) => annotation.sample !== sample),
    readMs: { ...state.readMs, [sample]: 0 },
    choice: null,
    generationDone: false,
    revealedAt: null,
    submitting: false,
    submitError: null,
    stage: state.stage === "compare" ? sample : state.stage,
  };
}

export const useBenchmarkTakeoverStore = create((set, get) => ({
  ...initialSessionState(),

  enableBenchmarkMode: async () => {
    const response = await Auth.fetchWithIdToken("/api/benchmark/consent", {});
    if (!response?.success) throw new Error("Could not enable Muse 2 Alpha. Please try again.");
  },

  isOpen: () => get().stage !== "closed",

  // ---------------------------------------------------------------- session
  beginSession: ({ sessionId, surface, onWriteAgain, onAbort, interceptedCard = null, precedingText = "" }) => {
    sessionRevision += 1;
    onWriteAgainRef = onWriteAgain || null;
    onAbortRef = onAbort || null;
    set({
      ...initialSessionState(),
      sessionId,
      surface,
      interceptedCard,
      canWriteAgain: typeof onWriteAgain === "function",
      precedingText: typeof precedingText === "string" ? precedingText : "",
      stage: "loading",
      startedAt: Date.now(),
    });
  },

  clearInterceptedCard: () => set({ interceptedCard: null }),

  resetSample: (sample, revision) => {
    const state = get();
    if (!["A", "B"].includes(sample) || !Number.isInteger(revision) || revision <= (state.sampleRevisions[sample] || 0) || state.stage === "closed" || state.ratingSubmitted || state.ratingClosedMessage) return false;
    sessionRevision += 1;
    set(replacementSampleState(state, sample, revision));
    return true;
  },

  appendSampleText: (sample, text) => {
    if (!text || (sample !== "A" && sample !== "B")) return;
    set((state) => ({
      samples: {
        ...state.samples,
        [sample]: { text: state.samples[sample].text + text },
      },
    }));
  },

  markSampleDone: (sample) => {
    if (sample !== "A" && sample !== "B") return;
    set((state) => ({ sampleDone: { ...state.sampleDone, [sample]: true } }));
  },

  // Show Option A while it is still streaming instead of holding the
  // "Writing..." loader for the whole generation. Only the stage moves here:
  // `revealedAt` (and so the reported rating duration) still starts when both
  // samples are complete. Both scorecards remain available while prose streams.
  revealSamples: () => {
    if (get().stage !== "loading") return;
    set({ stage: "A" });
  },

  markGenerationDone: () => {
    const state = get();
    if (state.stage === "closed" || state.generationDone) return;
    if (!state.samples.A.text || !state.samples.B.text) {
      state.failSession();
      return;
    }
    set({
      generationDone: true,
      sampleDone: { A: true, B: true },
      stage: state.stage === "loading" ? "A" : state.stage,
      revealedAt: Date.now(),
    });
  },

  failSession: (message, { preserveSamples = false, refund = null, historySaved = null } = {}) => {
    const state = get();
    if (state.stage === "closed") return;
    // A later terminal frame can confirm History/refund recovery after a
    // per-stream error. Retain the original reason and every captured token.
    const retained = preserveSamples || state.failure?.preserveSamples || ["A", "B"].some((sample) => !!state.samples[sample]?.text);
    set({
      failure: {
        message: state.failure?.message || message || GENERATION_FAILED_MESSAGE,
        preserveSamples: retained,
        refund: refund || state.failure?.refund || null,
        historySaved: historySaved ?? state.failure?.historySaved ?? null,
      },
      ...(retained ? { generationDone: true, stage: "compare" } : {}),
    });
  },

  // Older servers signal the completed survivor this way. It is not the
  // writer's preference and must never authorize manuscript insertion.
  fallbackToSample: (sample, refund = null, historySaved = null) => {
    if (sample !== "A" && sample !== "B") return;
    const refundMessage = refund?.status === "pending"
      ? " Any charged credits are queued for return."
      : refund?.amount > 0 ? " Your credits have been returned." : "";
    get().failSession(`The comparison couldn't finish.${refundMessage}`, { preserveSamples: true, refund, historySaved });
  },

  goToStage: (stage) => {
    set({ stage });
  },

  // ------------------------------------------------------------ rating draft
  setScore: (sample, key, value) =>
    set((state) => ({
      scores: { ...state.scores, [sample]: { ...state.scores[sample], [key]: value } },
    })),

  setAiIsms: (sample, text) => set((state) => ({ aiIsms: { ...state.aiIsms, [sample]: text } })),

  addAnnotation: (annotation) => set((state) => ({ annotations: [...state.annotations, annotation] })),

  replaceAnnotation: (original, updated) => set((state) => ({ annotations: state.annotations.flatMap((annotation) => annotation === original ? (updated ? [updated] : []) : [annotation]) })),

  removeAnnotation: (index) => set((state) => ({ annotations: state.annotations.filter((_, position) => position !== index) })),

  setChoice: (choice) => set({ choice }),

  setDecisionReason: (decisionReason) => set({ decisionReason }),

  sampleRated: (sample) => {
    const scores = get().scores[sample];
    return BENCHMARK_CRITERIA.every(({ key }) => scores[key] >= 1);
  },

  finalReady: () => {
    const state = get();
    return !state.failure && !state.ratingClosedMessage && state.generationDone && state.sampleRated("A") && state.sampleRated("B") && !!state.choice;
  },

  // ---------------------------------------------------------------- telemetry
  // Per-rating summary counters, sent with the rating payload on submit.
  addDwell: (sample, ms, revision = get().sampleRevisions[sample]) => {
    if (!ms || ms <= 0 || (sample !== "A" && sample !== "B")) return;
    if (revision !== get().sampleRevisions[sample]) return;
    const rounded = Math.round(ms);
    set((state) => ({ readMs: { ...state.readMs, [sample]: state.readMs[sample] + rounded } }));
  },

  recordCopyAttempt: () => {
    set((state) => ({ copyAttempts: state.copyAttempts + 1 }));
  },

  recordFocusBlur: () => {
    set((state) => ({ focusBlurCount: state.focusBlurCount + 1 }));
  },

  recordFeedbackPaste: () => {
    set((state) => ({ feedbackPasteEvents: state.feedbackPasteEvents + 1 }));
  },

  // ------------------------------------------------------------------ submit
  submitRating: async () => {
    const state = get();
    if (!state.sessionId || !state.finalReady() || state.submitting || state.ratingSubmitted) {
      return { ok: false, reason: "invalid_state" };
    }
    // Everything after an await is only valid for the session that started
    // the request. The user can close the takeover, or a new generation can
    // open a fresh session, while the response is still on the wire.
    const { sessionId, choice } = state;
    const revision = sessionRevision;
    const owns = () => get().sessionId === sessionId && sessionRevision === revision;
    set({ submitting: true, submitError: null });
    try {
      const annotationText = { A: renderedProseText(state.samples.A.text), B: renderedProseText(state.samples.B.text) };
      const res = await Auth.fetchWithIdToken(`/api/benchmark/sessions/${sessionId}/rating`, {
        choice,
        sampleRevisions: state.sampleRevisions,
        scores: state.scores,
        aiIsms: state.aiIsms,
        feedbackText: state.decisionReason,
        annotations: state.annotations.map((annotation) => {
          const resolved = resolveSourceAnnotation(state.samples[annotation.sample].text, annotation);
          return {
            sample: resolved.sample, tag: resolved.tag, text: resolved.text,
            ...(resolved.custom != null ? { custom: resolved.custom } : {}),
            // Preserve an unmapped tag as quoted feedback, using the server's
            // existing positionless format. Never invent or send invalid offsets.
            ...(annotationHasPosition(annotationText[resolved.sample], resolved)
              ? { start: resolved.start, end: resolved.end, coordinateSpace: ANNOTATION_COORDINATE_SPACE } : {}),
          };
        }),
        annotationText,
        coordinateSpace: ANNOTATION_COORDINATE_SPACE,
        readMsA: state.readMs.A,
        readMsB: state.readMs.B,
        ratingDurationMs: Date.now() - (state.revealedAt || Date.now()),
        copyAttempts: state.copyAttempts,
        feedbackPasteEvents: state.feedbackPasteEvents,
        focusBlurCount: state.focusBlurCount,
      });
      if (!owns()) return { ok: false, reason: "stale" };
      set({ submitting: false, ratingSubmitted: true, submittedChoice: ["A", "B", "neither"].includes(res?.choice) ? res.choice : choice, refund: res?.refund || null, submittedKeptOutput: res?.keptOutput ?? null });
      return { ok: true, res };
    } catch (e) {
      if (!owns()) return { ok: false, reason: "stale" };
      let parsed = null;
      try {
        parsed = JSON.parse(e?.responseText);
      } catch (parseError) {
        parsed = null;
      }
      const code = typeof parsed?.error === "string" ? parsed.error : null;
      if (code === "comparison_samples_changed") {
        set({ submitting: false, submitError: null });
        get().failSession("Resume this comparison to finish your feedback.", { preserveSamples: true });
        return { ok: false, reason: code };
      }
      const ratingClosedMessage = code === "not_ratable" && parsed?.status === "failed"
        ? "This comparison couldn't finish, so it can no longer accept a rating. Your feedback is still here to copy before you close."
        : SESSION_CLOSED_MESSAGES[code] || null;
      // A structured response can be a temporary finalization race, a service
      // failure, or rejected validation. None is permission to delete feedback.
      const message = code === "invalid_rating_body"
        ? "Your feedback could not be saved. Please shorten very long feedback or tags and try again. Your feedback is still here."
        : ratingClosedMessage || SUBMIT_RETRY_MESSAGE;
      set({ submitting: false, submitError: message, ratingClosedMessage });
      return { ok: false, reason: code || "network" };
    }
  },

  // `sessionId` may be passed explicitly: the completion step can run after
  // the takeover was closed (and the store reset) mid thank-you beat.
  sendKeep: async (kept, sessionId = get().sessionId) => {
    if (!sessionId) return;
    try {
      await Auth.fetchWithIdToken(`/api/benchmark/sessions/${sessionId}/keep`, { kept });
    } catch (e) {
      // Non-blocking behavioral signal; swallow errors.
    }
  },

  // Submit the "neither" rating, then re-fire the original generation request
  // (with benchmarkRetry: true) and reset the takeover into loading.
  writeAgain: async () => {
    const result = await get().submitRating();
    if (!result.ok) return result;
    if (get().submittedKeptOutput !== null) {
      get().close();
      return result;
    }
    const replay = onWriteAgainRef;
    // The old stream is finished; its abort handle must not be what a close
    // during the replay's loading state calls.
    onAbortRef = null;
    set({
      ...initialSessionState(),
      surface: get().surface,
      precedingText: get().precedingText,
      stage: "loading",
      startedAt: Date.now(),
    });
    if (replay) {
      // A replay hands back its own abort so closing while it is still
      // loading (no session announced yet) stops the new generation.
      const abort = replay();
      if (typeof abort === "function") onAbortRef = abort;
    } else {
      get().close();
    }
    return result;
  },

  close: () => {
    sessionRevision += 1;
    const state = get();
    // Closing mid-generation must also stop the generation. Otherwise the
    // stream keeps running server-side and the next Draft is refused with
    // "The AI is busy with something else".
    if ((!state.generationDone || state.failure) && onAbortRef) {
      try {
        onAbortRef();
      } catch (e) {
        // aborting is best-effort; never block the close
      }
    }
    onAbortRef = null;
    onWriteAgainRef = null;
    set({ ...initialSessionState() });
  },
}));

export default useBenchmarkTakeoverStore;
